import * as React from 'react';
import type { ErrorInfo } from 'react';

import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import logger from 'js/app/loggerSingleton';
import Retracked from 'js/lib/retracked';

type Props = {
  componentName: string;
  errorComponent?: JSX.Element;
};

export default class ErrorBoundaryWithTagging extends React.LegacyComponentWithChildren<Props> {
  state = {
    hasError: false,
  };

  static contextTypes = {
    _eventData: PropTypes.object,
    _withTrackingData: PropTypes.func,
  };

  declare context: LegacyContextType<typeof ErrorBoundaryWithTagging.contextTypes>;

  static getDerivedStateFromError() {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(error, errorInfo);
    Sentry.captureException(error, {
      extra: { errorInfo },
      tags: { source: this.props.componentName },
    });
    const { _eventData } = this.context;
    Retracked.trackComponent(_eventData, {}, this.props.componentName, 'error_thrown');
  }

  render() {
    if (this.state.hasError) {
      return this.props.errorComponent || null;
    }
    return this.props.children;
  }
}
